/**
 * What an edit DID, in words, for the undo and redo message (2026-11-02, the keyframe editing audit; operator: undo
 * messages should "name the edit" - they said "Undid a change to "Keys"").
 *
 * The editor names the edits it knows by intent (paste, cut, duplicate, copy, nudge, split) through
 * `timelineHistory.label`. Everything else is named here from the two versions of the timeline, so no edit anywhere is
 * left saying nothing: what was added, deleted, moved or changed. Pure.
 */
import type { ColorMarkerStep, Timeline, TimelineStep } from '@opbuttons/schema';

type Found = { laneId: string; step: TimelineStep };

function stepsOf(t: Timeline): Map<string, Found> {
  const m = new Map<string, Found>();
  for (const l of t.lanes) for (const s of l.steps) m.set(s.id, { laneId: l.id, step: s });
  return m;
}

/** "Blue keyframe", "3 keyframes", "1 change", "4 changes". `nameColour` names a keyframe's colour. */
export function stepsWord(steps: readonly TimelineStep[], nameColour: (s: ColorMarkerStep) => string): string {
  const keys = steps.every((s) => s.kind === 'colorMarker');
  if (steps.length === 1) return keys ? `${nameColour(steps[0] as ColorMarkerStep)} keyframe` : '1 change';
  return `${steps.length} ${keys ? 'keyframes' : 'changes'}`;
}

/** The edit that turned `from` into `to`, e.g. "move Blue keyframe", "delete 3 keyframes", "Red keyframe to Blue". */
export function describeEdit(from: Timeline, to: Timeline, nameColour: (s: ColorMarkerStep) => string): string {
  const a = stepsOf(from); const b = stepsOf(to);
  const added: TimelineStep[] = []; const removed: TimelineStep[] = []; const moved: TimelineStep[] = [];
  const changed: Array<{ was: TimelineStep; now: TimelineStep }> = [];
  for (const [id, x] of b) {
    const old = a.get(id);
    if (!old) { added.push(x.step); continue; }
    if (old.step === x.step && old.laneId === x.laneId) continue;
    const { position: p0, ...rest0 } = old.step;
    const { position: p1, ...rest1 } = x.step;
    const relocated = old.laneId !== x.laneId || Math.abs(p0 - p1) > 1e-9;
    const other = JSON.stringify(rest0) !== JSON.stringify(rest1);
    if (other) changed.push({ was: old.step, now: x.step });
    else if (relocated) moved.push(x.step);
  }
  for (const [id, x] of a) if (!b.has(id)) removed.push(x.step);
  const word = (s: readonly TimelineStep[]) => stepsWord(s, nameColour);
  const kinds = [added, removed, moved, changed].filter((x) => x.length > 0).length;
  if (kinds === 1) {
    if (added.length) return `add ${word(added)}`;
    if (removed.length) return `delete ${word(removed)}`;
    if (moved.length) return `move ${word(moved)}`;
    const c = changed[0]!;
    if (changed.length === 1 && c.was.kind === 'colorMarker' && c.now.kind === 'colorMarker') {
      const before = nameColour(c.was); const after = nameColour(c.now);
      if (before !== after) return `${before} keyframe to ${after}`;
      if (!!c.was.arrive !== !!c.now.arrive) return `${c.now.arrive ? 'split' : 'join'} ${after} keyframe`;
    }
    return `change ${word(changed.map((x) => x.now))}`;
  }
  if (kinds > 1) return `edit ${word([...added, ...removed, ...moved, ...changed.map((x) => x.now)])}`;
  if (from.lanes.length !== to.lanes.length) {
    const d = to.lanes.length - from.lanes.length;
    return `${d > 0 ? 'add' : 'delete'} ${Math.abs(d)} lane${Math.abs(d) === 1 ? '' : 's'}`;
  }
  if (JSON.stringify(from.sections) !== JSON.stringify(to.sections)) return 'change the sections';
  if (JSON.stringify(from.lanes.map((l) => ({ ...l, steps: 0 }))) !== JSON.stringify(to.lanes.map((l) => ({ ...l, steps: 0 })))) return 'change a lane';
  return `change "${to.name}"`;
}
